import { useEffect, useRef, useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import type { Algorithm, PipelineStage, Problem } from '../types'

interface Props {
  problem: Problem
  algo: Algorithm
  stage: PipelineStage
  onSelectStage: (id: string) => void
}

// Concatenate every stage into one runnable-looking script with section banners.
export function fullProgram(algo: Algorithm): string {
  const bar = '# ' + '='.repeat(62)
  const header = `${bar}\n# MLMate · ${algo.name}\n# Full program — every pipeline stage in order\n${bar}\n`
  const body = algo.pipeline
    .map((s, i) => `${bar}\n# Step ${i + 1} · ${s.label}\n${bar}\n${s.code}`)
    .join('\n\n')
  return header + '\n' + body + '\n'
}

export const STATIC_BUNDLES = new Set(['churn-xgb', 'churn-rf', 'fraud-iso', 'fraud-xgb'])

export function downloadProgram(algo: Algorithm) {
  if (STATIC_BUNDLES.has(algo.id)) {
    const a = document.createElement('a')
    a.href = `/mlmate/${algo.id}.zip`
    a.download = `mlmate_${algo.id.replace(/-/g, '_')}.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
    return
  }
  const blob = new Blob([fullProgram(algo)], { type: 'text/x-python' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `mlmate_${algo.id.replace(/-/g, '_')}.py`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export default function CodeViewer({ problem, algo, stage, onSelectStage }: Props) {
  const [view, setView] = useState<'problem' | 'stage' | 'full'>('problem')

  // picking a stage from the tree exits the problem/full-program views
  const lastStageId = useRef(stage.id)
  useEffect(() => {
    if (stage.id !== lastStageId.current) {
      lastStageId.current = stage.id
      setView('stage')
    }
  }, [stage.id])

  // opening a different algorithm starts back at the problem intro
  useEffect(() => {
    lastStageId.current = stage.id
    setView('problem')
  }, [algo.id])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* problem intro + stage tabs + full program + download */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-ink-700 px-3 py-2">
        <button
          onClick={() => setView('problem')}
          className={
            'rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ' +
            (view === 'problem'
              ? 'bg-teal-500/25 text-teal-300'
              : 'text-mut hover:bg-ink-800 hover:text-white')
          }
        >
          ◈ Problem
        </button>

        <div className="mx-1 h-4 w-px bg-ink-700" />

        {algo.pipeline.map((s, i) => {
          const active = view === 'stage' && s.id === stage.id
          return (
            <button
              key={s.id}
              onClick={() => {
                setView('stage')
                onSelectStage(s.id)
              }}
              className={
                'rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ' +
                (active ? 'bg-accent-500/25 text-accent-300' : 'text-mut hover:bg-ink-800 hover:text-white')
              }
            >
              <span className="mr-1 opacity-60">{i + 1}</span>
              {s.label}
            </button>
          )
        })}

        <div className="mx-1 h-4 w-px bg-ink-700" />

        <button
          onClick={() => setView('full')}
          className={
            'rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ' +
            (view === 'full'
              ? 'bg-fuchsia-500/25 text-fuchsia-300'
              : 'text-mut hover:bg-ink-800 hover:text-white')
          }
        >
          {'</>'} Full program
        </button>
        <button
          onClick={() => downloadProgram(algo)}
          title={STATIC_BUNDLES.has(algo.id) ? 'Download the full project bundle (.zip)' : 'Download the complete program as a .py file'}
          className="ml-auto rounded-md border border-accent-500/50 bg-accent-500/10 px-2.5 py-1 text-[12px] font-semibold text-accent-300 transition-colors hover:bg-accent-500/25"
        >
          {STATIC_BUNDLES.has(algo.id) ? '⬇ Download .zip' : '⬇ Download .py'}
        </button>
      </div>

      {view === 'problem' ? (
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-400">The problem</div>
          <h2 className="mt-1 text-lg font-semibold leading-snug text-white">{problem.blurb}</h2>
          <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-mut">{problem.realWorld}</p>

          <div className="mt-5 text-[11px] font-semibold uppercase tracking-widest text-accent-400">
            The approach · {algo.name}
          </div>
          <p className="mt-1 max-w-2xl text-[13.5px] leading-relaxed text-mut">{algo.summary}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-md bg-ink-800 px-2.5 py-1 text-[12px] text-mut">
              📊 {algo.dataset.name} · {algo.dataset.rows.toLocaleString('en-US')} rows · {algo.dataset.features} features
            </span>
            <span className="rounded-md bg-ink-800 px-2.5 py-1 text-[12px] text-mut">
              🎯 {algo.sampleMetric.name} {algo.sampleMetric.value}
            </span>
          </div>

          <p className="mt-6 text-[12px] leading-relaxed text-mut/80">
            Walk the stages <span className="font-medium text-accent-300">1 Dataset → {algo.pipeline.length} Predict</span>{' '}
            to see how the solution is built, then press <span className="font-medium text-accent-300">▶ Run pipeline</span>{' '}
            to watch it execute for real.
          </p>
        </div>
      ) : view === 'full' ? (
        <>
          <div className="shrink-0 border-b border-ink-700 bg-ink-900/60 px-4 py-2">
            <p className="text-[12px] leading-relaxed text-mut">
              The complete program — all {algo.pipeline.length} stages stitched together in run order.{' '}
              {STATIC_BUNDLES.has(algo.id)
                ? 'Download the .zip to get the script, data generator, and README with setup instructions.'
                : 'Download it and run it on your own laptop.'}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <SyntaxHighlighter
              language="python"
              style={oneDark}
              showLineNumbers
              lineNumberStyle={{ color: '#4a4a58', minWidth: '2.4em' }}
              customStyle={{ margin: 0, background: 'transparent', fontSize: 13.5, padding: '16px 18px' }}
              codeTagProps={{ style: { fontFamily: 'JetBrains Mono, ui-monospace, monospace' } }}
            >
              {fullProgram(algo)}
            </SyntaxHighlighter>
          </div>
        </>
      ) : (
        <>
          {/* why */}
          <div className="shrink-0 border-b border-ink-700 bg-ink-900/60 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-accent-400">Why this step</div>
            <p className="mt-1 text-[13.5px] leading-relaxed text-mut">{stage.why}</p>
          </div>

          {/* code */}
          <div className="min-h-0 flex-1 overflow-auto">
            <SyntaxHighlighter
              language={stage.language ?? 'python'}
              style={oneDark}
              customStyle={{ margin: 0, background: 'transparent', fontSize: 13.5, padding: '16px 18px' }}
              codeTagProps={{ style: { fontFamily: 'JetBrains Mono, ui-monospace, monospace' } }}
            >
              {stage.code}
            </SyntaxHighlighter>
          </div>
        </>
      )}
    </div>
  )
}
